import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

// API base URL with intelligent detection for mobile testing
const getApiBaseUrl = () => {
  // Production: use environment variable
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_BASE_URL || 'https://knotcord.com';
  }
  
  // Development: intelligent detection
  const hostname = window.location.hostname;
  
  // Localhost or LAN IP: always use relative URL so setupProxy.js handles it
  // This avoids Safari CORS issues and doesn't require Django on 0.0.0.0
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return '';
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Try to get cached user from sessionStorage to avoid flash
  const getCachedUser = () => {
    try {
      const cached = sessionStorage.getItem('cachedUser');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };

  const [user, setUser] = useState(getCachedUser());
  const [loading, setLoading] = useState(!getCachedUser()); // If we have cached user, don't show loading

  // Request deduplication - prevent multiple auth checks within 1 second
  const lastAuthCheckRef = React.useRef(0);
  const authCheckInProgressRef = React.useRef(false);

  // Wrap functions in useCallback to create stable references
  const checkAuthStatus = useCallback(async () => {
    // Deduplicate: skip if request is already in progress
    if (authCheckInProgressRef.current) {
      return;
    }

    // Deduplicate: skip if last check was less than 1 second ago
    const now = Date.now();
    if (now - lastAuthCheckRef.current < 1000) {
      return;
    }

    lastAuthCheckRef.current = now;
    authCheckInProgressRef.current = true;
    // IMPORTANT: Don't check for sessionid cookie via document.cookie because
    // SESSION_COOKIE_HTTPONLY = True makes it invisible to JavaScript.
    // The browser will automatically send the cookie with fetch requests.
    // Just make the API call and let the backend validate the session.

    try {
      const response = await fetch(`${API_BASE_URL}/zoulang/account/profile/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Cache user data to avoid loading flash on navigation
        sessionStorage.setItem('cachedUser', JSON.stringify(data.user));
      } else {
        setUser(null);
        sessionStorage.removeItem('cachedUser');
      }
    } catch (error) {
      setUser(null);
      sessionStorage.removeItem('cachedUser');
    } finally {
      setLoading(false);
      authCheckInProgressRef.current = false; // Reset flag after request completes
    }
  }, []); // Empty deps - uses stable setUser and setLoading from useState

  const login = useCallback((userData) => {
    setUser(userData);
    // Cache user data for instant loading
    sessionStorage.setItem('cachedUser', JSON.stringify(userData));
  }, []); // Empty deps - uses stable setUser from useState

  const logout = useCallback(async () => {
    try {
      // Call the logout API endpoint
      const response = await fetch(`${API_BASE_URL}/zoulang/account/logout/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        // Logout successful
      } else {
        // Continue with local logout even if API call fails
      }
    } catch (error) {
      // Continue with local logout even if API call fails
    } finally {
      // Always clear user state and cache regardless of API response
      setUser(null);
      sessionStorage.removeItem('cachedUser');
    }
  }, []); // Empty deps - uses stable setUser from useState

  // Check if user is logged in on app start
  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - checkAuthStatus is stable with empty deps

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    checkAuthStatus
  }), [user, loading, login, logout, checkAuthStatus]); // Include all dependencies

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};